import React from 'react';
import { Table, Button, Modal, Form, Input, Select, Checkbox, notification, Typography, Space } from 'antd';
import { PlusOutlined, EditOutlined, ApartmentOutlined, ExpandAltOutlined } from '@ant-design/icons';

import request from '../../lib/request';
import { useCompanyId, CompanySelector } from '../../lib/books';

export default function DepartmentsPage() {
  const { companyId } = useCompanyId();
  const [data, setData] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [modalOpen, setModalOpen] = React.useState(false);
  const [editing, setEditing] = React.useState(null);
  const [parentContext, setParentContext] = React.useState(null);
  const [formCompanyId, setFormCompanyId] = React.useState(null);
  const [expandedKeys, setExpandedKeys] = React.useState([]);
  const [form] = Form.useForm();

  /** Every department that has at least one child - the only rows that render an expand arrow. */
  const getParentIds = (list) => {
    const ids = new Set();
    list.forEach((d) => {
      if (d.parent_department_id) ids.add(d.parent_department_id);
    });
    return [...ids];
  };

  const fetchDepartments = (onLoaded) => {
    if (!companyId) return;
    setLoading(true);
    request
      .get('/departments', { company_id: companyId })
      .then((r) => r.json())
      .then((r) => {
        if (r.status) {
          setData(r.data);
          if (onLoaded) onLoaded(r.data);
        }
      })
      .finally(() => setLoading(false));
  };

  React.useEffect(() => {
    setData([]);
    fetchDepartments((loaded) => setExpandedKeys(getParentIds(loaded)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const openCreate = () => {
    setEditing(null);
    setParentContext(null);
    form.resetFields();
    setFormCompanyId(companyId);
    form.setFieldsValue({ company_id: companyId });
    setModalOpen(true);
  };

  /** Pre-fills company + parent from the Group department being expanded, so adding a child is a one-field form. */
  const openCreateChild = (parent) => {
    setEditing(null);
    setParentContext(parent);
    form.resetFields();
    setFormCompanyId(parent.company_id);
    form.setFieldsValue({ company_id: parent.company_id, parent_department_id: parent.id });
    setModalOpen(true);
  };

  const openEdit = (record) => {
    setEditing(record);
    setParentContext(null);
    form.setFieldsValue(record);
    setFormCompanyId(record.company_id);
    setModalOpen(true);
  };

  const handleSave = () => {
    form.validateFields().then((values) => {
      const call = editing ? request.put(`/departments/${editing.id}`, values) : request.post('/departments', values);
      call
        .then((r) => r.json())
        .then((r) => {
          if (r.status) {
            notification.success({ message: editing ? 'Department updated' : 'Department created' });
            setModalOpen(false);
            fetchDepartments(() => {
              // Reveal the row that was just filed under a parent, on top of whatever was already expanded.
              if (values.parent_department_id) {
                setExpandedKeys((prev) => Array.from(new Set([...prev, values.parent_department_id])));
              }
            });
          } else {
            notification.error({ message: r.message });
          }
        });
    });
  };

  const expandAll = () => setExpandedKeys(getParentIds(data));

  /** Descendants of `id` (per parent_department_id) - excluded from its own parent options to prevent a cycle. */
  const getDescendantIds = (id) => {
    const descendants = new Set();
    let frontier = [id];
    while (frontier.length) {
      const children = data.filter((d) => frontier.includes(d.parent_department_id)).map((d) => d.id);
      children.forEach((c) => descendants.add(c));
      frontier = children;
    }
    return descendants;
  };

  const excludedParentIds = editing ? getDescendantIds(editing.id).add(editing.id) : new Set();
  const parentOptions = data
    .filter((d) => d.is_group && d.company_id === formCompanyId && !excludedParentIds.has(d.id))
    .map((d) => ({ value: d.id, label: d.name }));

  /** Flat list -> nested tree (via parent_department_id), for the Table's built-in tree rendering. */
  const buildTree = (list) => {
    const nodes = Object.fromEntries(list.map((d) => [d.id, { ...d, key: d.id }]));
    const roots = [];
    list.forEach((d) => {
      const node = nodes[d.id];
      const parent = d.parent_department_id && nodes[d.parent_department_id];
      if (parent) {
        parent.children = parent.children || [];
        parent.children.push(node);
      } else {
        roots.push(node);
      }
    });
    return roots;
  };

  const columns = [
    { title: 'Name', dataIndex: 'name' },
    { title: 'Group?', dataIndex: 'is_group', render: (v) => (v ? 'Yes' : 'No') },
    {
      title: '',
      key: 'actions',
      render: (_, record) => (
        <Space>
          {record.is_group && (
            <Button icon={<ApartmentOutlined />} size="small" onClick={() => openCreateChild(record)}>
              Add Sub Department
            </Button>
          )}
          <Button icon={<EditOutlined />} size="small" onClick={() => openEdit(record)}>
            Edit
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between' }}>
        <Typography.Title level={4} style={{ margin: 0 }}>
          Departments
        </Typography.Title>
        <Space>
          <CompanySelector />
          <Button icon={<ExpandAltOutlined />} onClick={expandAll}>
            Expand All
          </Button>
          <Button type="primary" icon={<PlusOutlined />} disabled={!companyId} onClick={openCreate}>
            New Department
          </Button>
        </Space>
      </Space>

      <Table
        rowKey="id"
        columns={columns}
        dataSource={buildTree(data)}
        loading={loading}
        pagination={false}
        expandable={{ expandedRowKeys: expandedKeys, onExpandedRowsChange: setExpandedKeys }}
      />

      <Modal
        title={editing ? 'Edit Department' : parentContext ? `New Sub-Department under "${parentContext.name}"` : 'New Department'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={handleSave}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="company_id" hidden rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="name" label="Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="parent_department_id" label="Parent Department" tooltip="Only Group departments can be a parent">
            <Select allowClear disabled={!!parentContext} placeholder="None (top-level)" options={parentOptions} />
          </Form.Item>
          <Form.Item name="is_group" valuePropName="checked">
            <Checkbox>Is a Group (has sub-departments)</Checkbox>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
